import { useState } from 'react';
import { streamTypes, bitrateOptions } from '@/mocks/registerRadio';
import type { RadioFormData, StreamingMode } from '../types';
import { validateStreamUrl } from '../types';

interface StreamUrlFormProps {
  formData: RadioFormData;
  updateField: (field: keyof RadioFormData, value: string) => void;
  errors: Record<string, string>;
}

const rfwHostingBenefits = [
  { icon: 'ri-broadcast-line', label: 'Estación de streaming', desc: 'Servidores profesionales con AutoDJ, SSL y monitoreo 24/7.' },
  { icon: 'ri-disc-line', label: 'AutoDJ', desc: 'Programación automática sin depender de una computadora encendida.' },
  { icon: 'ri-play-list-2-line', label: 'Playlists', desc: 'Gestioná tus listas de reproducción desde el panel.' },
  { icon: 'ri-hard-drive-2-line', label: 'Almacenamiento', desc: 'Espacio para tu biblioteca de audio según el plan contratado.' },
  { icon: 'ri-calendar-check-line', label: 'Programación', desc: 'Horarios y bloques programables con AutoDJ.' },
  { icon: 'ri-bar-chart-grouped-line', label: 'Monitoreo', desc: 'Oyentes simultáneos, geolocalización y estadísticas en tiempo real.' },
  { icon: 'ri-customer-service-2-line', label: 'Soporte operativo', desc: 'Asistencia técnica para tu streaming y configuración.' },
  { icon: 'ri-admin-line', label: 'Panel de administración', desc: 'Gestioná todo desde un solo lugar sin depender de terceros.' },
  { icon: 'ri-play-circle-line', label: 'URL pública', desc: 'Stream URL lista para usar en tu página de RFW y compartir.' },
];

export default function StreamUrlForm({ formData, updateField, errors }: StreamUrlFormProps) {
  const [streamUrlDirty, setStreamUrlDirty] = useState(false);
  const [streamUrlValidationError, setStreamUrlValidationError] = useState<string | null>(null);

  const isExternal = formData.streamingMode === 'EXTERNAL_STREAM';
  const isManaged = formData.streamingMode === 'RFW_MANAGED';

  const handleModeChange = (mode: StreamingMode) => {
    updateField('streamingMode' as keyof RadioFormData, mode);
    if (mode === 'RFW_MANAGED') {
      updateField('streamUrl', '');
      updateField('streamType', '');
      updateField('bitrate', '');
      updateField('providerName', '');
      updateField('streamFormat', '');
      setStreamUrlDirty(false);
      setStreamUrlValidationError(null);
    }
  };

  const handleStreamUrlChange = (value: string) => {
    updateField('streamUrl', value);
    setStreamUrlDirty(true);
    if (value.trim()) {
      const result = validateStreamUrl(value);
      setStreamUrlValidationError(result.valid ? null : result.error || null);
    } else {
      setStreamUrlValidationError(null);
    }
  };

  return (
    <div className="space-y-5">
      {/* ── Streaming mode selection ── */}
      <div>
        <p className="text-xs font-semibold text-foreground-700 mb-3">
          ¿Cómo transmitirás tu radio?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* EXTERNAL_STREAM */}
          <button
            type="button"
            onClick={() => handleModeChange('EXTERNAL_STREAM')}
            className={`relative rounded-2xl p-4 text-left cursor-pointer transition-all duration-200 border-2 ${
              isExternal
                ? 'border-primary-500 bg-primary-50/30 ring-2 ring-primary-200'
                : 'border-background-200 bg-background-50 hover:border-background-300 hover:shadow-sm'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                isExternal ? 'bg-primary-100' : 'bg-background-100'
              }`}>
                <i className={`ri-link text-lg ${
                  isExternal ? 'text-primary-600' : 'text-foreground-400'
                }`}></i>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-foreground-800 mb-0.5">
                  Ya tengo streaming
                </p>
                <p className="text-[11px] text-foreground-500 leading-relaxed">
                  Ya cuento con un proveedor o servidor de streaming y quiero registrar mi estación en RadioFrecuenciaWeb.
                </p>
                {isExternal && (
                  <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-bold">
                    <i className="ri-check-line text-[9px]"></i>
                    Streaming externo
                  </span>
                )}
              </div>
            </div>
          </button>

          {/* RFW_MANAGED */}
          <button
            type="button"
            onClick={() => handleModeChange('RFW_MANAGED')}
            className={`relative rounded-2xl p-4 text-left cursor-pointer transition-all duration-200 border-2 ${
              isManaged
                ? 'border-accent-500 bg-accent-50/30 ring-2 ring-accent-200'
                : 'border-background-200 bg-background-50 hover:border-background-300 hover:shadow-sm'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                isManaged ? 'bg-accent-100' : 'bg-background-100'
              }`}>
                <i className={`ri-cloud-line text-lg ${
                  isManaged ? 'text-accent-600' : 'text-foreground-400'
                }`}></i>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-foreground-800 mb-0.5">
                  Quiero streaming con RadioFrecuenciaWeb
                </p>
                <p className="text-[11px] text-foreground-500 leading-relaxed">
                  Quiero contratar el servicio completo de streaming, AutoDJ y administración desde RadioFrecuenciaWeb.
                </p>
                {isManaged && (
                  <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-bold">
                    <i className="ri-check-line text-[9px]"></i>
                    Streaming administrado por RFW
                  </span>
                )}
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* ── EXTERNAL_STREAM form ── */}
      {isExternal && (
        <>
          <div className="bg-background-100/70 rounded-xl p-4 border border-background-200">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-information-line text-primary-600 text-sm"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-800 mb-1">¿Qué es la Stream URL?</p>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  Es la dirección pública de tu servidor de streaming. Conectá tu Stream URL actual para que RFW la reproduzca dentro de la plataforma. La validación técnica real la realiza nuestro backend.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
              URL del Stream <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              value={formData.streamUrl}
              onChange={(e) => handleStreamUrlChange(e.target.value)}
              placeholder="https://tu-servidor.com:8000/stream"
              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 transition-all ${
                (errors.streamUrl || (streamUrlDirty && streamUrlValidationError))
                  ? 'border-red-300 bg-red-50/30 focus:ring-red-400/60 focus:border-red-400'
                  : 'border-background-200 bg-background-50 focus:ring-primary-400/60 focus:border-primary-400'
              }`}
            />
            {errors.streamUrl && <p className="text-[11px] text-red-500 mt-1">{errors.streamUrl}</p>}
            {streamUrlDirty && streamUrlValidationError && !errors.streamUrl && (
              <p className="text-[11px] text-red-500 mt-1">{streamUrlValidationError}</p>
            )}
            <p className="text-[10px] text-foreground-400 mt-1">
              No incluyas credenciales en la URL. Solo la dirección pública del stream.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                Formato <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.streamType}
                onChange={(e) => updateField('streamType', e.target.value)}
                className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-foreground-900 focus:outline-none focus:ring-2 transition-all appearance-none bg-background-50 ${
                  errors.streamType
                    ? 'border-red-300 bg-red-50/30 focus:ring-red-400/60 focus:border-red-400'
                    : 'border-background-200 focus:ring-primary-400/60 focus:border-primary-400'
                }`}
              >
                <option value="">Selecciona el formato</option>
                {streamTypes.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              {errors.streamType && <p className="text-[11px] text-red-500 mt-1">{errors.streamType}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                Bitrate <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.bitrate}
                onChange={(e) => updateField('bitrate', e.target.value)}
                className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-foreground-900 focus:outline-none focus:ring-2 transition-all appearance-none bg-background-50 ${
                  errors.bitrate
                    ? 'border-red-300 bg-red-50/30 focus:ring-red-400/60 focus:border-red-400'
                    : 'border-background-200 focus:ring-primary-400/60 focus:border-primary-400'
                }`}
              >
                <option value="">Selecciona el bitrate</option>
                {bitrateOptions.map((b) => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </select>
              {errors.bitrate && <p className="text-[11px] text-red-500 mt-1">{errors.bitrate}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                Proveedor
              </label>
              <input
                type="text"
                value={formData.providerName}
                onChange={(e) => updateField('providerName', e.target.value)}
                placeholder="Ej: Tu proveedor de streaming"
                className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                Codec / Formato técnico
              </label>
              <input
                type="text"
                value={formData.streamFormat}
                onChange={(e) => updateField('streamFormat', e.target.value)}
                placeholder="Ej: MP3 128kbps, AAC+"
                className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all"
              />
            </div>
          </div>

          {/* Legal: external streaming */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-scales-3-line text-amber-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-amber-800 mb-2">Streaming externo — Responsabilidad de la radio</p>
                <p className="text-xs text-amber-700 leading-relaxed">
                  <strong>Cada radio es responsable de contratar y mantener su propio proveedor de streaming.</strong> La radio conecta su Stream URL para que RFW la reproduzca dentro de la plataforma. Si la URL falla, se cae, no transmite o el proveedor externo tiene problemas, RadioFrecuenciaWeb no es responsable por esa interrupción.
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── RFW_MANAGED info ── */}
      {isManaged && (
        <div className="space-y-4">
          {/* Benefits grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {rfwHostingBenefits.map((b) => (
              <div
                key={b.label}
                className="flex items-start gap-2.5 p-3 rounded-xl bg-background-50 border border-background-200"
              >
                <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                  <i className={`${b.icon} text-accent-600 text-sm`}></i>
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground-800 mb-0.5">{b.label}</p>
                  <p className="text-[10px] text-foreground-500 leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Provisioning notice */}
          <div className="bg-accent-50/50 border border-accent-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-tools-line text-accent-600 text-sm"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-foreground-800 mb-1">
                  RadioFrecuenciaWeb se encarga de todo
                </p>
                <p className="text-xs text-foreground-500 leading-relaxed mb-2">
                  Al seleccionar esta modalidad, RFW aprovisiona tu estación de streaming. No necesitás ingresar ninguna Stream URL ahora.
                </p>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  El aprovisionamiento incluye: creación de la estación, configuración técnica, AutoDJ, almacenamiento según tu plan, playlists, monitoreo y soporte. Recibirás tu Stream URL pública cuando la estación esté lista.
                </p>
              </div>
            </div>
          </div>

          {/* Legal: RFW managed */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-shield-check-line text-emerald-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-emerald-800 mb-2">Streaming administrado por RFW</p>
                <p className="text-xs text-emerald-700 leading-relaxed">
                  Al contratar RFW como proveedor de streaming, <strong>nosotros administramos tu servidor</strong>. AutoDJ, servidores profesionales, SSL, estadísticas y soporte incluidos. Vos solo te preocupás por hacer radio. El aprovisionamiento se realiza mediante nuestro backend una vez confirmado tu plan y pago.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}